// JournalLogModal — the confirm/review modal for Mirror-driven Diário capture.
// Voice flow: propose_journal_log -> journal-extract -> prefill (source 'voice').
//
// Mirrors the Focus/Sleep mold: AgendaBottomSheet shell, editable fields hydrated
// from a prefill, writes via the existing useJournal.saveEntry (now with source),
// then emits a capture recap. `content` is REQUIRED and `mode` is ALWAYS set (the
// journal_entries NOT NULL + CHECK trap); `mood` is optional (1-5).
//
// Perf: useJournal (fetches 50 entries on mount) is DEFERRED — the outer
// component returns null until `open`, so nothing runs on every Mirror render
// even though useModuleCapture keeps this in the tree.

import { useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useJournal, type JournalMode } from "@/hooks/useJournal";
import { AgendaBottomSheet } from "@/components/agenda/CreateEventModal";
import { emitCaptureRecap } from "@/lib/capture/captureRecapBus";
import { sfx } from "@/lib/sfx";

export interface JournalPrefill {
  content: string;
  mode: JournalMode;
  /** 1-5, or null when no mood was expressed. */
  mood: number | null;
  /** 'voice' for spoken capture. */
  source: "voice";
  /** Per-turn idempotency key ('mirror_msg_<action_id>') — replay-safe write. */
  client_action_id?: string;
  /** Optional raw phrase for the "you said" reference. */
  transcriptRaw?: string | null;
}

interface JournalLogModalProps {
  open: boolean;
  prefill: JournalPrefill | null;
  onClose: () => void;
  onCreated: () => void;
}

const MODE_COLOR: Record<JournalMode, string> = {
  free: "hsl(187 72% 50%)",
  reflection: "hsl(45 90% 60%)",
  gratitude: "hsl(340 72% 60%)",
};
const MOOD_EMOJIS = ["😩", "😐", "🙂", "😊", "🔥"];
const MODES: JournalMode[] = ["free", "reflection", "gratitude"];

export default function JournalLogModal({ open, prefill, onClose, onCreated }: JournalLogModalProps) {
  if (!open || !prefill) return null;
  return <JournalLogModalInner prefill={prefill} onClose={onClose} onCreated={onCreated} />;
}

function JournalLogModalInner({
  prefill,
  onClose,
  onCreated,
}: {
  prefill: JournalPrefill;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useLanguage();
  const { saveEntry } = useJournal();

  const [content, setContent] = useState(prefill.content ?? "");
  const [mode, setMode] = useState<JournalMode>(prefill.mode ?? "free");
  // moodIndex: -1 = none; 0-4 maps to the stored 1-5.
  const [moodIndex, setMoodIndex] = useState(
    prefill.mood && prefill.mood >= 1 && prefill.mood <= 5 ? prefill.mood - 1 : -1,
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error(t("journal.log.saveError"));
      return;
    }
    setSaving(true);
    try {
      const saved = await saveEntry({
        mode,
        content: content.trim(),
        mood: moodIndex >= 0 ? moodIndex + 1 : undefined,
        source: prefill.source,
        clientActionId: prefill.client_action_id,
      });
      if (!saved) {
        sfx.error("module");
        toast.error(t("journal.log.saveError"));
        return;
      }
      sfx.confirm("module");
      emitCaptureRecap({
        kind: "journal",
        title: t("mirror.recap.journal"),
        subtitle: content.trim().slice(0, 40),
      });
      onCreated();
      onClose();
    } catch (e) {
      console.error("[JournalLogModal] save failed:", e);
      sfx.error("module");
      toast.error(t("journal.log.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(187 72% 50% / 0.15)",
  };
  const labelStyle = { color: "hsl(187 72% 50% / 0.55)" };

  const footer = (
    <button
      onClick={handleSave}
      disabled={saving}
      className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide transition-all duration-200 active:scale-[0.98]"
      style={{
        background: saving ? "hsl(187 72% 50% / 0.25)" : "hsl(187 72% 50%)",
        color: "hsl(var(--background))",
      }}
    >
      {saving ? t("journal.saving") : t("journal.log.save")}
    </button>
  );

  return (
    <AgendaBottomSheet open onClose={onClose} title={t("journal.log.title")} footer={footer}>
      {prefill.transcriptRaw && (
        <div
          className="mb-4 rounded-xl px-3 py-2.5"
          style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.18)" }}
        >
          <span
            className="text-[9px] font-display tracking-[0.18em] uppercase mb-1 block"
            style={{ color: "hsl(187 72% 60% / 0.75)" }}
          >
            {t("agenda.voice.youSaid")}
          </span>
          <p className="text-[12px] font-display text-muted-foreground italic leading-snug">
            &ldquo;{prefill.transcriptRaw}&rdquo;
          </p>
        </div>
      )}

      {/* Content (required) */}
      <label className="block mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("journal.log.content")}
        </span>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={4}
          className="w-full rounded-xl px-4 py-3 text-[14px] font-display text-foreground placeholder:text-muted-foreground outline-none resize-none"
          style={inputStyle}
        />
      </label>

      {/* Mode (always set) */}
      <div className="mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("journal.chooseMode")}
        </span>
        <div className="grid grid-cols-3 gap-2">
          {MODES.map((m) => {
            const selected = mode === m;
            const color = MODE_COLOR[m];
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className="py-2.5 rounded-xl font-display text-[12px] font-semibold transition-all active:scale-[0.98]"
                style={{
                  background: selected ? `${color}1f` : "hsl(222 18% 10% / 0.6)",
                  border: selected ? `1px solid ${color}55` : "1px solid hsl(var(--border) / 0.2)",
                  color: selected ? color : "hsl(218 14% 55%)",
                }}
              >
                {t(`journal.mode.${m}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mood (optional) */}
      <div className="mb-2">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("journal.moodOptional")}
        </span>
        <div className="flex gap-2">
          {MOOD_EMOJIS.map((emoji, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setMoodIndex(moodIndex === i ? -1 : i)}
              className="flex-1 py-2 rounded-xl text-[20px] transition-all active:scale-[0.95]"
              style={{
                background: moodIndex === i ? "hsl(187 72% 50% / 0.12)" : "transparent",
                border: moodIndex === i ? "1px solid hsl(187 72% 50% / 0.4)" : "1px solid hsl(var(--border) / 0.15)",
                opacity: moodIndex >= 0 && moodIndex !== i ? 0.4 : 1,
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </AgendaBottomSheet>
  );
}
